import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/Clubs.css";

const PLANS = [
  { key: "explorer", name: "Explore", price: "$199*", sub: "/ M" },
  { key: "adventure", name: "Adventure", price: "$349*", sub: "/ M", featured: true },
  { key: "elite", name: "Elite", price: "$599*", sub: "/ M" },
];

const ROWS = [
  { label: "Days of Annual Access", values: ["15", "22", "29"] },
  { label: "Compact Vehicles", values: ["✓", "✓", "✓"] },
  { label: "Standard Vehicles", values: ["✓", "✓", "✓"] },
  { label: "Recreational Vehicles", values: ["—", "✓", "✓"] },
  { label: "Premium Vehicles", values: ["—", "—", "✓"] },
  { label: "Performance Vehicles", values: ["—", "—", "✓"] },
  { label: "Insurance Included", values: ["✓", "✓", "✓"] },
  { label: "Orientation & walkthrough", values: ["✓", "✓", "✓"] },
  { label: "Booking Class", values: ["1", "2", "3"] },
  { label: "Delivery Service", values: ["Optional", "Optional", "Allowance"] },
  { label: "Set-up Service", values: ["Optional", "Optional", "Optional"] },
];

function Cell({ value }) {
  const isCheck = value === "✓";
  const isDash = value === "—";
  return (
    <span className={`cellVal ${isCheck ? "is-check" : ""} ${isDash ? "is-dash" : ""}`}>
      {isCheck ? "✓" : value}
    </span>
  );
}

const TIERS = [
  {
    key: "explore",
    name: "Explore",
    days: "15 Days Per Year",
    annualPrice: "$2,199",
    monthlyPrice: "$199 / M",
    monthlyNote: "+ Tax OAC*",
    tagline: "Perfect for weekend getaways",
    featured: false,
    access: {
      headline: "All Categories",
      detail: "Standard & Compact Models",
    },
  },
  {
    key: "adventure",
    name: "Adventure",
    days: "22 Days Per Year",
    annualPrice: "$3,849",
    monthlyPrice: "$349 / M",
    monthlyNote: "+ Tax OAC*",
    tagline: "Best for families and active outdoor users.",
    featured: true,
    badge: "Most Popular",
    access: {
      headline: "All Categories",
      detail: "Recreational Models",
    },
  },
  {
    key: "elite",
    name: "Elite",
    days: "29 Days Per Year",
    monthlyPrice: "$599 / M",
    monthlyNote: "+ Tax OAC*",
    annualPrice: "$6,599",
    tagline: "Exceptional Value, Premium Convenience.",
    featured: false,
    access: {
      headline: "All Categories",
      detail: "Premium & Performance Models",
    },
  },
];

// PLANS uses "explorer" but your route/page is "/explore".
// This helper maps explorer -> explore, and leaves other keys unchanged.
const planRoute = (key) => (key === "explorer" ? "explore" : key);

export default function ClubsPage() {
  const defaultPlanKey = PLANS.find((p) => p.featured)?.key || PLANS[0].key;
  const [selectedPlan, setSelectedPlan] = useState(defaultPlanKey);

  const selectedIndex = useMemo(
    () => Math.max(0, PLANS.findIndex((p) => p.key === selectedPlan)),
    [selectedPlan]
  );

  const activePlan = PLANS[selectedIndex];

  return (
    <div className="clubsPage">
      <header className="clubsHero">
        <div className="container clubsHero__inner"></div>
      </header>

      
      {/* Video (below Clubs hero) */}
      <section className="section section--muted">
        <div className="container">
          <div className="videoBlock">
            <div className="videoBlock__copy">
              <div className="videoBlock__eyebrow">Watch</div>
              <h2 className="videoBlock__title">See how the club works</h2>
              <p className="videoBlock__text">
                A quick walkthrough of what membership looks like — from choosing a tier
                to booking your next weekend on the water, trail, or snow.
              </p>
            </div>

            <div className="videoBlock__frame" aria-label="YouTube video">
              <iframe
                src="https://www.youtube.com/embed/ikUUHMo65iU"
                title="Try Play Go - How it works"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </div>
        </div>
      </section>


      {/* Membership tiers (from Club Tiers) */}
      <section className="section">
        <div className="container">
          <div className="section__head">
            <div>
              <h2 className="section__title">Choose Your Club.</h2>
              <p className="section__subtitle">
                Pick the access level that matches how often you want to get out.
              </p>
            </div>
          </div>

          <div className="ctCards">
            {TIERS.map((t) => (
              <article key={t.key} className={`ctCard ${t.featured ? "is-featured" : ""}`}>
                {t.featured && <div className="ctCard__badge">{t.badge || "Most Popular"}</div>}

                <div className="ctCard__top">
                  <h3 className="ctCard__name">{t.name}</h3>
                  <div className="ctCard__days">{t.days}</div>
                  <p className="ctCard__tag">{t.tagline}</p>
                </div>

                <br />

                <div className="ctPriceBlock">
                  <div className="ctPriceBlock__label">Monthly Payment - 12 Month Term.</div>
                  <div className="ctPriceBlock__value">
                    {t.monthlyPrice} <span className="ctMuted">{t.monthlyNote}</span>
                  </div>
                </div>

                <br />

                <div className="ctCard__or">OR</div>

                <div className="ctPrices">
                  <div className="ctPriceBlock">
                    <div className="ctPriceBlock__label">Annual Payment</div>
                    <div className="ctPriceBlock__value">
                      {t.annualPrice} <span className="ctMuted">+ Tax</span>
                    </div>
                  </div>
                </div>

                <div className="ctAccess">
                  <div className="ctAccess__title">Vehicle Access</div>
                  <div className="ctAccess__headline">{t.access.headline}</div>
                  <div className="ctAccess__detail">{t.access.detail}</div>
                </div>

                <div className="ctCard__actions">
                  {/* ✅ Join buttons route to /explore, /adventure, /elite */}
                  <Link
                    className={`btn ${t.featured ? "btn--primary" : "btn--ghost"} ctCard__btn`}
                    to={`/${t.key}`}
                  >
                    Join {t.name}
                  </Link>

                  <Link className="link ctCard__link" to="/clubs">
                    Compare
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* THE PERFECT GIFT */}
      <section className="section perfectGift">
        <div className="container">
          <div className="perfectGift__header">
            <span className="perfectGift__eyebrow">
              Give the Gift of Adventure
            </span>

            <h2 className="perfectGift__title">The Perfect Gift.</h2>

            <p className="perfectGift__subtitle">
              Give someone the freedom to explore, play, and make
              unforgettable memories. The perfect gift for anyone who
              loves adventure.
            </p>
          </div>

          <div className="perfectGift__content">
            <script async src="https://js.stripe.com/v3/pricing-table.js"></script>

            <stripe-pricing-table
              pricing-table-id="prctbl_1UTMjBLf216OKSFEZARVj4Gu"
              publishable-key="pk_live_51RRFEwLf216OKSFEIQFqn2yO6auDEr9BK4COHcSbk7imVYAr5qPJolMjZldMYVFoLKAUYbdbOmhVQbuULTrtuBew00fPpOnSZn"
            ></stripe-pricing-table>
          </div>
        </div>
      </section>
      {/* EXPLORE MEMBERSHIP UPGRADE OFFER */}
<div className="giftUpgrade">
  <div className="giftUpgrade__icon" aria-hidden="true">
    ↗
  </div>

  <div className="giftUpgrade__content">
    <h3 className="giftUpgrade__title">
      Your Adventure Is Just the Beginning.
    </h3>

    <p className="giftUpgrade__text">
      Loved your 2-day Explore membership? After completing your second
      day of use, you have <strong>30 days</strong> to apply the amount
      you paid toward a full Try Play Go membership.
    </p>

    <div className="giftUpgrade__footer">
      Try it. Love it. Upgrade.
    </div>
  </div>
</div>

      <main className="clubsMain">
        <section className="section">
          <div className="container">
            <div className="clubsHeaderRow">
              <div>
                <h2 className="section__title">Club Comparison</h2>
                <p className="section__subtitle"></p>
              </div>
            </div>


            {/* ===== Desktop / Tablet: Full Table ===== */}
            <div className="clubsTableWrap" aria-label="Membership comparison table">
              <table className="clubsTable">
                <thead>
                  <tr>
                    <th className="clubsTable__feature">Feature</th>

                    {PLANS.map((p) => (
                      <th
                        key={p.key}
                        className={`clubsTable__plan ${p.featured ? "is-featured" : ""}`}
                        scope="col"
                      >
                        <div className="planName">{p.name}</div>
                        <div className="planPrice">
                          <span className="planPrice__num">{p.price}</span>{" "}
                          <span className="planPrice__sub">{p.sub}</span>
                        </div>

                        {/* ✅ Choose buttons route to /explore, /adventure, /elite */}
                        <Link
                          className={`btn ${p.featured ? "btn--primary" : "btn--ghost"} planBtn`}
                          to={`/${planRoute(p.key)}`}
                        >
                          Choose {p.name}
                        </Link>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {ROWS.map((row) => (
                    <tr key={row.label}>
                      <th className="clubsTable__rowLabel" scope="row">
                        {row.label}
                      </th>
                      {row.values.map((v, idx) => (
                        <td key={`${row.label}-${idx}`} className="clubsTable__cell">
                          <Cell value={v} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ===== Mobile: Plan Picker + Feature List ===== */}
            <div className="clubsMobile" aria-label="Mobile membership comparison">
              <div className="planPicker" role="tablist" aria-label="Select a membership">
                {PLANS.map((p) => {
                  const isActive = p.key === selectedPlan;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={`planTab ${isActive ? "is-active" : ""} ${
                        p.featured ? "is-featured" : ""
                      }`}
                      onClick={() => setSelectedPlan(p.key)}
                    >
                      <div className="planTab__top">
                        <span className="planTab__name">{p.name}</span>
                        {p.featured && <span className="planTab__pill">Popular</span>}
                      </div>
                      <div className="planTab__price">
                        <span className="planTab__num">{p.price}</span>
                        <span className="planTab__sub">{p.sub}</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              <article className="mobileCard">
                <div className="mobileCard__head">
                  <div>
                    <div className="mobileCard__title">{activePlan.name}</div>
                    <div className="mobileCard__price">
                      <span className="mobileCard__num">{activePlan.price}</span>
                      <span className="mobileCard__sub">{activePlan.sub}</span>
                    </div>
                  </div>

                  {/* ✅ Mobile Choose button routes to /explore, /adventure, /elite */}
                  <Link
                    className={`btn ${activePlan.featured ? "btn--primary" : "btn--ghost"} mobileCard__btn`}
                    to={`/${planRoute(activePlan.key)}`}
                  >
                    Choose {activePlan.name}
                  </Link>
                </div>

                <div className="mobileList" role="list">
                  {ROWS.map((row) => (
                    <div key={row.label} className="mobileRow" role="listitem">
                      <div className="mobileRow__label">{row.label}</div>
                      <div className="mobileRow__value">
                        <Cell value={row.values[selectedIndex]} />
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
